import { useCallback, useEffect, useRef, useState } from 'react';
import { api, type CatalogueItem } from '../api/client';
import { friendlyError } from '../lib/errors';

export type CatalogueStatus = 'idle' | 'loading' | 'error';

export interface UseCatalogueResult {
  items: CatalogueItem[];
  status: CatalogueStatus;
  errorMessage: string | null;
  optimisticAdd: (item: CatalogueItem) => void;
  replace: (id: string, item: CatalogueItem) => void;
  remove: (id: string) => void;
  refresh: () => Promise<void>;
}

export function useCatalogue(): UseCatalogueResult {
  const [items, setItems] = useState<CatalogueItem[]>([]);
  const [status, setStatus] = useState<CatalogueStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const refresh = useCallback(async () => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setStatus('loading');
    setErrorMessage(null);
    try {
      const resp = await api.listCatalogue(ctrl.signal);
      if (!ctrl.signal.aborted) {
        // Backend may return either a raw array or a wrapped
        // `{ items: [...], limit, offset }` envelope. Normalise to the
        // array the rest of the hook (and consumers) expect.
        const items = Array.isArray(resp) ? resp : (resp as any)?.items ?? [];
        setItems(items as CatalogueItem[]);
        setStatus('idle');
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      setStatus('error');
      setErrorMessage(friendlyError(err).message);
    }
  }, []);

  const optimisticAdd = useCallback((item: CatalogueItem) => {
    setItems((prev) => [item, ...prev]);
  }, []);

  const replace = useCallback((id: string, item: CatalogueItem) => {
    setItems((prev) => prev.map((existing) => (existing.id === id ? item : existing)));
  }, []);

  const remove = useCallback((id: string) => {
    setItems((prev) => prev.filter((existing) => existing.id !== id));
  }, []);

  // Refresh when the tab regains focus so catalogue stays fresh.
  useEffect(() => {
    const onFocus = () => {
      void refresh();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refresh]);

  return { items, status, errorMessage, optimisticAdd, replace, remove, refresh };
}

/**
 * Save a generated reference to the catalogue.
 */
export function useSaveToCatalogue(): {
  saving: boolean;
  errorMessage: string | null;
  save: (input: {
    generated_id: string;
    title: string;
    notes: string;
    tags: string[];
  }) => Promise<CatalogueItem | null>;
  reset: () => void;
} {
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const save = useCallback(
    async (input: {
      generated_id: string;
      title: string;
      notes: string;
      tags: string[];
    }) => {
      setSaving(true);
      setErrorMessage(null);
      try {
        const item = await api.saveToCatalogue(input);
        return item;
      } catch (err) {
        setErrorMessage(friendlyError(err).message);
        return null;
      } finally {
        setSaving(false);
      }
    },
    [],
  );

  const reset = useCallback(() => {
    setErrorMessage(null);
    setSaving(false);
  }, []);

  return { saving, errorMessage, save, reset };
}
